// Session policy (docs/refer/design/sessionhandling-v1.md): a password once at the start of the
// day, a 12-hour end (SecureGate `session_expires_at`), a 4-hour idle timeout, no refresh tokens.
//
// Pure module: used by middleware, API guards, the Auth.js callbacks and client components.
// Times are epoch milliseconds.

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

function idleLimitFromEnv(): number {
  // Server-only override for staging tests (e.g. SESSION_IDLE_MINUTES=5). Default 4 hours.
  const minutes = Number(process.env.SESSION_IDLE_MINUTES);
  return Number.isFinite(minutes) && minutes > 0 ? minutes * MINUTE_MS : 4 * HOUR_MS;
}

/** No user action for this long ends the session (starting value; tuned from idle_gap data). */
export const IDLE_LIMIT_MS = idleLimitFromEnv();

/** Session end when SecureGate sent no `session_expires_at`: login time + 12 hours. */
export const SESSION_FALLBACK_MS = 12 * HOUR_MS;

/** The expiry warning appears this long before the session end. */
export const EXPIRY_WARNING_MS = 15 * MINUTE_MS;

/** Allowed clock skew when comparing `auth_time` with the reauth request time. */
export const CLOCK_SKEW_S = 60;

/** Real activity re-issues the activity cookie at most this often (idle check accurate to this). */
export const ACTIVITY_REFRESH_MS = 15 * MINUTE_MS;

/** A session writes `last_activity_at` to `scappsessions` at most this often. */
export const DB_TOUCH_MS = HOUR_MS;

/** A return after at least this long without activity is logged as an `idle_gap` event. */
export const IDLE_GAP_LOG_MS = 30 * MINUTE_MS;

/** Ended sessions are reloaded from the database at most this often per server instance. */
export const REVOKED_CACHE_TTL_MS = MINUTE_MS;

export type SessionState = "ok" | "idle" | "expired";

/** `scappsessions.end_reason` values. */
export type SessionEndReason = "signed_out" | "ended_by_admin" | "idle_timeout" | "expired";

/** A decoded session JWT; only sessionId, lastActivityAt and sessionExpiresAt are read. */
type SessionTimes = Record<string, unknown>;

/** The activity cookie (src/lib/sessionActivity.ts), once its signature is verified. */
export type ActivityStamp = { sessionId: string; lastActivityAt: number; lastDbTouchAt: number };

/** Last real user action: the newer of the login time on the token and a matching activity cookie. */
export function lastActivityOf(token: SessionTimes, activity?: ActivityStamp | null): number | null {
  const fromToken = typeof token.lastActivityAt === "number" ? token.lastActivityAt : null;
  const fromCookie = activity && activity.sessionId === token.sessionId ? activity.lastActivityAt : null;
  if (fromToken === null) return fromCookie;
  return fromCookie === null ? fromToken : Math.max(fromToken, fromCookie);
}

/** A token issued before this policy has no session id; the middleware ends it (sign in once). */
export function isPrePolicyToken(token: SessionTimes): boolean {
  return typeof token.sessionId !== "string";
}

/**
 * "expired" once the 12-hour end has passed, "idle" after IDLE_LIMIT_MS without a user action,
 * otherwise "ok". A missing activity cookie never counts as fresh activity: the idle clock then
 * runs from the login time on the token, and a session with no activity record at all is idle.
 * No token, or a pre-policy token: "ok" here — the middleware ends pre-policy tokens
 * (isPrePolicyToken) before every page and API request.
 */
export function evaluateSession(
  token: SessionTimes | null | undefined,
  activity?: ActivityStamp | null,
  now: number = Date.now()
): SessionState {
  if (!token) return "ok";
  if (typeof token.sessionExpiresAt === "number" && now >= token.sessionExpiresAt) return "expired";
  const last = lastActivityOf(token, activity);
  if (last === null) return isPrePolicyToken(token) ? "ok" : "idle";
  if (now - last >= IDLE_LIMIT_MS) return "idle";
  return "ok";
}

/** Session end from the ID token's `session_expires_at` (unix seconds), else login time + 12 h. */
export function sessionExpiresAtFrom(sessionExpiresAtClaim: unknown, loginAt: number): number {
  const seconds = Number(sessionExpiresAtClaim);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : loginAt + SESSION_FALLBACK_MS;
}
